import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Clock4, Users, Sparkles, Video, MapPin, Loader2, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import SpeakerAvatar from "../components/SpeakerAvatar";
import WebinarRegistrationModal from "../components/WebinarRegistrationModal";
import { api } from "../lib/api";
import { formatWebinarDate, formatTimeRange } from "../lib/webinarFormat";
import {
  trackWebinarsPageView,
  trackWebinarCardView,
  trackWebinarRegistrationStart,
} from "../lib/tracking";

export default function Webinars() {
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registerFor, setRegisterFor] = useState(null);
  const [successId, setSuccessId] = useState(null);

  useEffect(() => {
    document.title = "Webinarii AI pentru educație | FPED";
    trackWebinarsPageView();
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.get("/webinars/public").then((r) => {
      if (cancelled) return;
      const list = r.data?.webinars || [];
      setWebinars(list);
      list.forEach((w) => trackWebinarCardView(w));
    }).catch(() => {
      toast.error("Nu am putut încărca webinariile. Te rugăm să încerci din nou.");
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const openRegistration = (webinar) => {
    setSuccessId(null);
    setRegisterFor(webinar);
    trackWebinarRegistrationStart(webinar);
  };

  const onRegistered = (registrationId) => {
    setSuccessId(registrationId);
  };

  const heroWebinar = webinars[0] || null;
  const otherWebinars = webinars.slice(1);

  return (
    <div className="min-h-screen bg-[#F5F6F6]" data-testid="webinars-page">
      <SEO
        title="Webinarii AI pentru educație | FPED"
        description="Participă la webinarii gratuite despre utilizarea inteligenței artificiale în educație și află cum te poate ajuta AI în activitatea didactică."
        canonical="https://ai.fped.org/webinarii"
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 grain-bg opacity-50 pointer-events-none" />
          <div className="max-w-6xl mx-auto px-6 lg:px-8 pt-14 md:pt-20 pb-10">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#F0E7F4] px-3 py-1 text-xs font-semibold text-[#965EA7] uppercase tracking-widest">
              <Sparkles size={14} /> Sesiuni gratuite
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#373A45] mt-4 leading-[1.05]">
              Webinarii gratuite despre <span className="text-[#965EA7]">Inteligența Artificială</span> în educație
            </h1>
            <p className="mt-5 text-base md:text-lg text-[#6B7280] max-w-2xl">
              Sesiuni online de prezentare pentru cadrele didactice din România.
              Vezi cum poți folosi AI etic și practic la clasă, înainte să te înscrii
              la cursul complet acreditat.
            </p>
          </div>
        </section>

        {/* Webinar list */}
        <section className="max-w-6xl mx-auto px-6 lg:px-8 pb-20">
          {loading ? (
            <div className="bg-white rounded-3xl border border-[#E5E7EB] p-10 text-center text-[#6B7280]">
              <Loader2 className="animate-spin mx-auto mb-3" size={24} />
              Se încarcă webinariile...
            </div>
          ) : webinars.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              <FeaturedWebinarCard
                webinar={heroWebinar}
                onRegister={() => openRegistration(heroWebinar)}
              />
              {otherWebinars.length > 0 && (
                <div className="mt-12">
                  <h2 className="font-display text-2xl font-bold text-[#373A45] mb-5">
                    Alte webinarii disponibile
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {otherWebinars.map((w) => (
                      <SecondaryWebinarCard
                        key={w.id}
                        webinar={w}
                        onRegister={() => openRegistration(w)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </main>
      <Footer />

      {registerFor && (
        <WebinarRegistrationModal
          webinar={registerFor}
          successId={successId}
          onClose={() => { setRegisterFor(null); setSuccessId(null); }}
          onRegistered={onRegistered}
        />
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-white rounded-3xl border border-[#E5E7EB] p-10 md:p-14 text-center">
      <div className="inline-flex h-14 w-14 rounded-full bg-[#F0E7F4] items-center justify-center text-[#965EA7] mb-4">
        <CalendarDays size={24} />
      </div>
      <h2 className="font-display text-2xl font-bold text-[#373A45]">
        Niciun webinar programat momentan
      </h2>
      <p className="mt-2 text-[#6B7280] max-w-md mx-auto">
        Pregătim următoarea sesiune gratuită. Lasă-ne adresa de email pe pagina principală
        și te anunțăm imediat ce publicăm un webinar nou.
      </p>
    </div>
  );
}

function FeaturedWebinarCard({ webinar, onRegister }) {
  if (!webinar) return null;
  const isFull = webinar.status === "full"
    || (webinar.capacity > 0 && webinar.seats_remaining === 0);
  const detailHref = `/webinarii/${webinar.slug}`;
  return (
    <article
      className="bg-white rounded-3xl border border-[#E5E7EB] shadow-sm overflow-hidden"
      data-testid="webinar-featured"
      data-webinar-id={webinar.id}
    >
      <div className="grid grid-cols-1 md:grid-cols-5">
        <div className="md:col-span-3 p-7 md:p-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#F0E7F4] px-3 py-1 text-[11px] font-semibold text-[#965EA7] uppercase tracking-widest">
            <Sparkles size={12} /> Următorul webinar
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#373A45] mt-3 leading-tight">
            <Link to={detailHref} className="hover:text-[#965EA7] transition-colors">
              {webinar.title}
            </Link>
          </h2>
          {webinar.short_description && (
            <p className="mt-3 text-[#4b4f5a] leading-relaxed">{webinar.short_description}</p>
          )}

          <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <MetaRow icon={<CalendarDays size={16} />} label="Data" value={formatWebinarDate(webinar.date)} />
            <MetaRow
              icon={<Clock4 size={16} />}
              label="Ora"
              value={`${formatTimeRange(webinar.start_time, webinar.duration_minutes)} (${webinar.duration_minutes} min)`}
            />
            <MetaRow
              icon={webinar.location_type === "online" ? <Video size={16} /> : <MapPin size={16} />}
              label="Format"
              value={webinar.location_type === "online" ? "Online (link în email)" : "Fizic"}
            />
            <MetaRow
              icon={<Users size={16} />}
              label="Locuri disponibile"
              value={
                webinar.capacity > 0
                  ? `${webinar.seats_remaining} / ${webinar.capacity}`
                  : "Limitate"
              }
            />
          </dl>

          {webinar.speaker_name && (
            <div className="mt-6 flex items-center gap-3 pt-5 border-t border-[#E5E7EB]">
              <SpeakerAvatar name={webinar.speaker_name} photoUrl={webinar.speaker_photo_url} size={48} />
              <div>
                <div className="font-semibold text-[#373A45] text-sm">{webinar.speaker_name}</div>
                {webinar.speaker_title && (
                  <div className="text-xs text-[#6B7280]">{webinar.speaker_title}</div>
                )}
              </div>
            </div>
          )}

          <Link
            to={detailHref}
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#965EA7] hover:text-[#7A4986]"
            data-testid="webinar-featured-details"
          >
            Vezi detalii <ArrowRight size={15} />
          </Link>
        </div>

        <div className="md:col-span-2 bg-[#373A45] text-white p-7 md:p-10 flex flex-col justify-between">
          <div>
            <div className="text-white/70 text-xs uppercase tracking-widest">Înscriere</div>
            <div className="mt-2 font-display text-2xl font-bold">Participă gratuit</div>
            <p className="mt-3 text-white/70 text-sm leading-relaxed">
              Te înscrii în 30 de secunde și primești pe email confirmarea
              înscrierii. Linkul de acces îl trimitem cu o zi înainte de eveniment.
            </p>
          </div>
          <button
            type="button"
            onClick={onRegister}
            disabled={isFull}
            data-testid="webinar-register-cta-featured"
            className={`mt-6 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-semibold transition-all ${
              isFull
                ? "bg-white/20 text-white/50 cursor-not-allowed"
                : "bg-[#965EA7] hover:bg-[#7A4986] text-white hover:-translate-y-0.5"
            }`}
          >
            {isFull ? "Webinar complet" : "Înscrie-te gratuit"}
          </button>
        </div>
      </div>
    </article>
  );
}

function SecondaryWebinarCard({ webinar, onRegister }) {
  const isFull = webinar.status === "full"
    || (webinar.capacity > 0 && webinar.seats_remaining === 0);
  const detailHref = `/webinarii/${webinar.slug}`;
  return (
    <article
      className="bg-white rounded-3xl border border-[#E5E7EB] p-6 flex flex-col"
      data-testid="webinar-secondary"
      data-webinar-id={webinar.id}
    >
      <h3 className="font-display text-xl font-bold text-[#373A45] leading-snug">
        <Link to={detailHref} className="hover:text-[#965EA7] transition-colors">
          {webinar.title}
        </Link>
      </h3>
      {webinar.short_description && (
        <p className="mt-2 text-sm text-[#6B7280] leading-relaxed line-clamp-3">
          {webinar.short_description}
        </p>
      )}
      <dl className="mt-4 space-y-2">
        <MetaRow icon={<CalendarDays size={14} />} label="Data" value={formatWebinarDate(webinar.date)} small />
        <MetaRow
          icon={<Clock4 size={14} />}
          label="Ora"
          value={formatTimeRange(webinar.start_time, webinar.duration_minutes)}
          small
        />
        <MetaRow
          icon={<Users size={14} />}
          label="Locuri"
          value={webinar.capacity > 0 ? `${webinar.seats_remaining} / ${webinar.capacity}` : "Limitate"}
          small
        />
      </dl>
      <div className="mt-5 flex items-center gap-3">
        <button
          type="button"
          onClick={onRegister}
          disabled={isFull}
          data-testid="webinar-register-cta-secondary"
          className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
            isFull
              ? "bg-[#F5F6F6] text-[#9CA3AF] cursor-not-allowed"
              : "bg-[#965EA7] hover:bg-[#7A4986] text-white hover:-translate-y-0.5"
          }`}
        >
          {isFull ? "Complet" : "Înscrie-te gratuit"}
        </button>
        <Link
          to={detailHref}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#965EA7] hover:text-[#7A4986]"
          data-testid="webinar-secondary-details"
        >
          Detalii <ArrowRight size={14} />
        </Link>
      </div>
    </article>
  );
}

function MetaRow({ icon, label, value, small }) {
  return (
    <div className="flex items-start gap-2">
      <div className={`mt-0.5 text-[#965EA7] shrink-0`}>{icon}</div>
      <div className={small ? "text-xs" : "text-sm"}>
        <div className="text-[#6B7280]">{label}</div>
        <div className={`font-semibold text-[#373A45] ${small ? "" : "mt-0.5"}`}>{value}</div>
      </div>
    </div>
  );
}
